import { TranslateService } from '@ngx-translate/core';
import { DecimalPipe } from '@angular/common';
import {
  Component,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { AccountInfo } from 'src/app/shared/models/accountInfo.model';
import { BuildingModel } from 'src/app/shared/models/building.model';
import {
  BuildingCategories,
  ManageBuildingTypes,
} from 'src/app/shared/models/enum.model';
import { MunicipalityModel } from 'src/app/shared/models/municipality.model';
import { AddBuilding } from 'src/app/shared/ngxs/action/building.action';
import { FilterMunicipalitiesWithoutFiltering } from 'src/app/shared/ngxs/action/municipality.action';
import { AccountState } from 'src/app/shared/ngxs/state/account.state';
import { MunicipalityState } from 'src/app/shared/ngxs/state/municipality.state';
import * as _ from '../../../../node_modules/lodash';
import {
  EditBuilding,
  GetBuilding,
  GetBuildingCategories,
} from './../../shared/ngxs/action/building.action';
import { BuildingState } from './../../shared/ngxs/state/building.state';
import { ManageBuildingHelperService } from './../../shared/services/manage-building-helper.service';

export const MY_FORMATS = {
  parse: {
    dateInput: 'DD MMM YYYY',
  },
  display: {
    dateInput: 'YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-manage-building',
  templateUrl: './manage-building.component.html',
  styleUrls: ['./manage-building.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },

    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class ManageBuildingComponent implements OnInit, OnDestroy {
  @Input() type: ManageBuildingTypes = ManageBuildingTypes.Add;
  @Input() modelId: string;
  @Input() validCategories: string[] = [];

  @ViewChild('stepper', { static: false }) matStepper: MatStepper;

  @Select(AccountState.account)
  account$: Observable<AccountInfo>;

  isAdministrator = false;

  confirmed = false;
  building: BuildingModel;

  buildingCategories = BuildingCategories;

  basicGroup: FormGroup;
  cO2Group: FormGroup;
  detailsGroup: FormGroup;
  energyGroup: FormGroup;
  geometryGroup: FormGroup;
  investmentGroup: FormGroup;
  moneySavingGroup: FormGroup;
  coGroup: FormGroup;
  consumationGroup: FormGroup;
  dustGroup: FormGroup;
  nmvocGroup: FormGroup;
  noxGroup: FormGroup;
  pricePerInvestmentGroup: FormGroup;
  profitabilityGroup: FormGroup;
  so2Group: FormGroup;
  municipalities: MunicipalityModel[];
  manageBuildingTypes = ManageBuildingTypes;

  isVeles = false;
  isGjorcePetrov = false;
  velesId: string = '7f6be169-aeb9-48c7-bc80-0e0e499f4c82';
  gjorceId: string = '58e28c04-1d6d-4988-a8e9-93d8959cb4fc';

  private dialogRef = null;

  subscription = new Subscription();

  @Select(BuildingState.selected)
  building$: Observable<BuildingModel>;

  @Select(MunicipalityState.municipalities)
  municipalities$: Observable<MunicipalityModel[]>;

  @Select(BuildingState.selected)
  selected$: Observable<BuildingModel>;

  @Select(AccountState.isAuthorized)
  isAuthorized$: Observable<boolean>;

  isAuthorized = false;

  format = '1.2-2';
  formInitialized = false;

  constructor(
    private injector: Injector,
    private _formBuilder: FormBuilder,
    private dialog: MatDialog,
    private store: Store,
    private decPipe: DecimalPipe,
    private manageBuildingHelperService: ManageBuildingHelperService,
    private translateService: TranslateService
  ) {
    this.dialogRef = this.injector.get(MatDialogRef, null);
    const data = this.injector.get(MAT_DIALOG_DATA, null);

    if (data) {
      if (data.type) {
        this.type = data.type;
      }

      if (data.modelId) {
        this.modelId = data.modelId;

        this.store.dispatch(new GetBuilding(this.modelId));
      }
    }
  }

  isCategoryValid(categoryName: BuildingCategories) {
    const category = BuildingCategories[categoryName];
    const isValid =
      category &&
      (this.isAdministrator ||
        this.validCategories.includes(category.toString()));

    return isValid;
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  initFormGroups() {
    this.basicGroup = this.manageBuildingHelperService.initBasicGroup(
      this.building
    );

    this.cO2Group = this.manageBuildingHelperService.initCo2Group(
      this.building
    );

    this.energyGroup = this.manageBuildingHelperService.initEnergyGroup(
      this.building
    );
    this.geometryGroup = this.manageBuildingHelperService.initGeometryGroup(
      this.building
    );

    this.investmentGroup = this.manageBuildingHelperService.initInvestmentGroup(
      this.building
    );

    this.moneySavingGroup =
      this.manageBuildingHelperService.initMoneySavingsGroup(this.building);

    this.coGroup = this.manageBuildingHelperService.initCoGroup(this.building);

    this.consumationGroup =
      this.manageBuildingHelperService.initConsumationGroup(this.building);

    this.dustGroup = this.manageBuildingHelperService.initDustGroup(
      this.building
    );

    this.nmvocGroup = this.manageBuildingHelperService.initNmvocGroup(
      this.building
    );

    this.noxGroup = this.manageBuildingHelperService.initNoxGroup(
      this.building
    );

    this.pricePerInvestmentGroup =
      this.manageBuildingHelperService.initPricePerInvestmentGroup(
        this.building
      );

    this.profitabilityGroup =
      this.manageBuildingHelperService.initProfitabilityGroup(this.building);

    this.so2Group = this.manageBuildingHelperService.initSo2Group(
      this.building
    );

    this.detailsGroup = this.manageBuildingHelperService.initDetailsGroup(
      this.building
    );

    this.isVeles = this.building.municipalityId === this.velesId ? true : false;
    this.isGjorcePetrov =
      this.building.municipalityId === this.gjorceId ? true : false;

    this.formInitialized = true;
  }

  ngOnInit() {
    this.store.dispatch(new GetBuildingCategories());

    this.subscription.add(
      this.account$.subscribe((x) => {
        if (x) {
          if (x.userType.toString() === 'Administrator') {
            this.isAdministrator = true;
          }
        }
      })
    );

    this.subscription.add(
      this.municipalities$.subscribe((x) => {
        if (x) {
          this.municipalities = x;
        }
      })
    );
    this.subscription.add(
      this.isAuthorized$.subscribe((x) => {
        this.isAuthorized = x;
      })
    );

    this.subscription.add(
      this.building$.subscribe((x) => {
        if (x) {
          this.building = _.cloneDeep(x);
          this.initFormGroups();
        }
      })
    );

    if (this.type === ManageBuildingTypes.Add) {
      this.building = new BuildingModel();
      this.store.dispatch(
        new FilterMunicipalitiesWithoutFiltering(
          this.translateService.currentLang
        )
      );
      this.initFormGroups();
    }
  }

  goToDetails() {
    if (this.type !== ManageBuildingTypes.Details) {
      this.manageBuildingHelperService.setBasicData(
        this.building,
        this.basicGroup,
        this.energyGroup,
        this.geometryGroup
      );
    }

    this.matStepper.next();
  }

  goToTransmission() {
    if (this.type !== ManageBuildingTypes.Details) {
      this.manageBuildingHelperService.setDetailsData(
        this.building,
        this.detailsGroup
      );
    }
    this.matStepper.next();
  }

  goToInvestment() {
    if (this.type !== ManageBuildingTypes.Details) {
      this.manageBuildingHelperService.setGasData(
        this.building,
        this.coGroup,
        this.cO2Group,
        this.dustGroup,
        this.nmvocGroup,
        this.noxGroup,
        this.so2Group
      );
    }

    this.matStepper.next();
  }

  goToMoneySaving() {
    if (this.type !== ManageBuildingTypes.Details) {
      this.manageBuildingHelperService.setInvestmentData(
        this.building,
        this.investmentGroup,
        this.pricePerInvestmentGroup
      );
    }

    this.matStepper.next();
  }

  goToConsumption() {
    if (this.type !== ManageBuildingTypes.Details) {
      this.manageBuildingHelperService.setMoneySavingsData(
        this.building,
        this.moneySavingGroup
      );
    }

    this.matStepper.next();
  }

  goToProfitability() {
    if (this.type !== ManageBuildingTypes.Details) {
      this.manageBuildingHelperService.setConsumationData(
        this.building,
        this.consumationGroup
      );
    }

    this.matStepper.next();
  }

  confirmAndCreateBuilding() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Are you sure you want to create building?',
        text: 'Please confirm building data is correct.',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.manageBuildingHelperService.setProfitabilityData(
          this.building,
          this.profitabilityGroup
        );

        if (this.type === ManageBuildingTypes.Add) {
          this.store.dispatch(new AddBuilding(this.building));
          this.confirmed = true;
        } else if (this.type === ManageBuildingTypes.Update) {
          this.store.dispatch(new EditBuilding(this.building));
          this.confirmed = true;
        }
      }
    });
  }
  closeMainDialog() {
    return this.confirmed;
  }
  periodConstructionSelected(event: any) {
    this.basicGroup.controls['construction'].setValue(event);
  }
}
